import Firebase from './firebase.services';

function sortDeepArray(arr) {
  return arr.sort((a, b) => {
    let aTime = new Date(a.date).getTime();
    let bTime = new Date(b.date).getTime();
    if (aTime > bTime) {
      return 1;
    }
    if (aTime < bTime) {
      return -1;
    }
    return 0;
  });
}

function retrieveComments(commentDocument) {
  if (commentDocument && commentDocument.comments && Array.isArray(commentDocument.comments)) {
    return sortDeepArray(commentDocument.comments);
  } else {
    return [];
  }
}

class FirebasePostsServices {
  constructor() {
    this.postsCollection = 'posts';
    this.groupsCollection = 'groups';
    this.userPostsCollection = 'userPosts';
    this.userGroupsCollection = 'userGroups';
    this.groupPostsCollection = 'groupPosts';
    this.postCommentsCollection = 'postComments';
    this.commentsCollection = 'comments';
    this.wallCollection = 'wall';
  }

  async addPost({ text, mediaArray = [], groupData, coach } = {}) {
    try {
      let table = this.postsCollection;
      let docId = Firebase.getInstance().auth.currentUser.uid.toString();
      let post = {
        text,
        mediaArray,
        coach: !!coach,
        user: Firebase.getInstance().auth.currentUser.uid,
        viewCount: 0,
        postCount: 0,
        comments: [],
        commentCount: 0,
        likeArray: [],
        date: Math.floor(new Date().getTime() / 1000)
      };

      if (groupData) {
        table = this.groupPostsCollection;
        docId = groupData.id;
        post = {
          ...post,
          groupId: groupData.id,
          user: Firebase.getInstance().auth.currentUser.uid
        };
      }

      const postRef = Firebase.getInstance().getFBData({
        parentCollection: table,
        endCollection: this.userPostsCollection,
        parentDoc: docId
      });

      postRef.set({ ...post, id: postRef.id });

      if (groupData) {
        // Increment Post count
        return Firebase.getInstance()
          .updateFBData({
            values: {
              id: groupData.id,
              groupPostsCount: Firebase.getInstance().firebase.firestore.FieldValue.increment(1)
            },
            parentCollection: this.groupPostsCollection,
            endCollection: this.userGroupsCollection,
            parentDoc: `${groupData.userId}`
          })
          .then(doc => [null, doc])
          .catch(err => [err]);
      }

      return [null, postRef];
    } catch (err) {
      return [err];
    }
  }

  getPost({ post, groupId, useInHooks = false } = {}) {
    let table = this.postsCollection;
    let docId = post.user;
    if (groupId) {
      table = this.groupPostsCollection;
      docId = groupId;
    }
    const query = Firebase.getInstance().getFBData({
      parentCollection: table,
      endCollection: this.userPostsCollection,
      parentDoc: docId,
      id: post.id
    });

    if (useInHooks) {
      return query;
    }
    let some = query
      .get()
      .then(doc => [null, doc.data()])
      .catch(err => [err]);
    return some;
  }

  async updatePost({ post = {}, groupId } = {}) {
    let collection = this.postsCollection;
    let docId = post.user;

    if (groupId) {
      collection = this.groupPostsCollection;
      docId = groupId;
    }

    return Firebase.getInstance()
      .updateFBData({
        parentCollection: collection,
        endCollection: this.userPostsCollection,
        doc: docId,
        values: post
      })
      .then(res => [null, res])
      .catch(err => [err]);
  }

  async deletePost({ postId, groupId } = {}) {
    let batch = Firebase.getInstance().db.batch();
    let commentsCollectionRef = Firebase.getInstance()
      .db.collection(this.postCommentsCollection)
      .doc(postId)
      .collection(this.commentsCollection);
    await commentsCollectionRef.get().then(async function(querySnapshot) {
      querySnapshot.forEach(function(doc) {
        batch.delete(doc.ref);
      });
    });

    await batch.commit();

    let collection = this.postsCollection;

    if (groupId) {
      collection = this.groupPostsCollection;
    }

    Firebase.getInstance().deleteFBDoc({
      parentCollection: this.postCommentsCollection,
      doc: postId
    });

    return Firebase.getInstance()
      .deleteMultipleFBData({
        parentCollection: collection,
        endCollection: this.userPostsCollection,
        ids: [postId]
      })
      .then(res => [null, res])
      .catch(err => [err]);
  }

  getUserPosts({ useInHooks = false, recentsOnly = false, userId } = {}) {
    const query = Firebase.getInstance()
      .getFBCollectionData({
        parentCollection: this.postsCollection,
        endCollection: this.userPostsCollection,
        parentDoc: userId || Firebase.getInstance().auth.currentUser.uid
      })
      .orderBy('date', 'desc');

    if (useInHooks) {
      if (recentsOnly) {
        return query.limit(3);
      }

      return query;
    }

    return query
      .get()
      .then(querySnapshot => {
        const data = [];
        querySnapshot.forEach(doc => {
          data.push({
            id: doc.id,
            ...doc.data()
          });
        });

        return [null, data];
      })
      .catch(error => {
        return [error];
      });
  }

  getUserWall({ useInHooks = false, recentsOnly = false, queryOnly = false, userId } = {}) {
    const query = Firebase.getInstance()
      .getFBCollectionData({
        parentCollection: this.wallCollection,
        endCollection: this.postsCollection,
        parentDoc: userId
      })
      .orderBy('date', 'desc');

    if (queryOnly || useInHooks) {
      if (recentsOnly) {
        return query.limit(3);
      }

      return query;
    }

    return query
      .get()
      .then(querySnapshot => {
        const data = [];
        querySnapshot.forEach(doc => {
          data.push({
            ...doc.data()
          });
        });

        return [null, data];
      })
      .catch(error => {
        return [error];
      });
  }

  async likePost(post, groupId) {
    let collection = this.postsCollection;
    let docId = post.user;

    if (groupId) {
      collection = this.groupPostsCollection;
      docId = groupId;
    }

    const postRef = Firebase.getInstance().getFBData({
      parentCollection: collection,
      endCollection: this.userPostsCollection,
      parentDoc: docId,
      id: post.id
    });

    return postRef
      .update({
        likeArray: Firebase.getInstance().firebase.firestore.FieldValue.arrayUnion(Firebase.getInstance().auth.currentUser.uid)
      })
      .then(res => [null, res])
      .catch(err => [err]);
  }

  async unlikePost(post, userId, groupId) {
    let collection = this.postsCollection;
    let docId = post.user;

    if (groupId) {
      collection = this.groupPostsCollection;
      docId = groupId;
    }

    const postRef = Firebase.getInstance().getFBData({
      parentCollection: collection,
      endCollection: this.userPostsCollection,
      parentDoc: docId,
      id: post.id
    });

    return postRef
      .update({
        likeArray: Firebase.getInstance().firebase.firestore.FieldValue.arrayRemove(userId)
      })
      .then(res => [null, res])
      .catch(err => [err]);
  }

  async commentPost({ post, comment, groupId } = {}) {
    try {
      const commentObject = { ...comment, likeArray: [] };
      const [postCommentsError, commentId] = await Firebase.getInstance()
        .addFBData({
          parentCollection: this.postCommentsCollection,
          endCollection: this.commentsCollection,
          doc: post.id,
          values: commentObject
        })
        .then(docRef => [null, docRef.id])
        .catch(err => [err]);

      if (postCommentsError) {
        return [postCommentsError];
      }

      /**
       * This section is for updating the preview comments (maximum total of 3) in post object.
       * This would be better to be moved in GCF so that we won't do unnecessary calls for the client side.
       * And doing this kind of updates can lead to outdated post comments when 2 or more users do comment at
       * nearly the same time.
       */

      let table = this.postsCollection;
      let docId = post.user;

      if (groupId) {
        table = this.groupPostsCollection;
        docId = groupId;
      }

      const postRef = Firebase.getInstance()
        .db.collection(table)
        .doc(docId)
        .collection(this.userPostsCollection)
        .doc(post.id);

      const postGet = await postRef.get();
      const postDoc = postGet.data();

      let updatedPostComments = retrieveComments(postDoc);

      if (updatedPostComments.length === 3) {
        updatedPostComments.shift();
      }

      updatedPostComments.push(commentObject);

      const [postUpdateError] = await postRef
        .update({
          comments: updatedPostComments,
          commentCount: Firebase.getInstance().firebase.firestore.FieldValue.increment(1)
        })
        .then(res => [null, res])
        .catch(err => [err]);

      if (postUpdateError) {
        return [postUpdateError];
      }

      return [null, { id: commentId }];
    } catch (err) {
      return [err];
    }
  }

  async updatePostPreviewComments({ post, groupId, isDeletion = false } = {}) {
    /**
     * Update post latest 3 comments preview
     * This can be move to GCF as well.
     */
    const [latestCommentsError, latestComments] = await Firebase.getInstance()
      .getFBCollectionData({
        endCollection: this.commentsCollection,
        parentCollection: this.postCommentsCollection,
        parentDoc: post.id
      })
      .orderBy('date', 'desc')
      .limit(3)
      .get()
      .then(querySnapshot => {
        const data = [];
        querySnapshot.forEach(doc => {
          data.push({
            id: doc.id,
            ...doc.data()
          });
        });

        return [null, data];
      })
      .catch(error => {
        return [error];
      });

    if (latestCommentsError) {
      return [latestCommentsError];
    }

    let table = this.postsCollection;
    let docId = post.user;

    if (groupId) {
      table = this.groupPostsCollection;
      docId = groupId;
    }

    const additionalValues = {};

    if (isDeletion) {
      additionalValues.commentCount = Firebase.getInstance().firebase.firestore.FieldValue.increment(-1);
    }

    return Firebase.getInstance()
      .updateFBData({
        doc: docId,
        endCollection: this.userPostsCollection,
        parentCollection: table,
        values: {
          id: post.id,
          comments: latestComments,
          ...additionalValues
        }
      })
      .then(res => [null, res])
      .catch(err => [err]);
  }

  async updatePostComment({ post, comment, groupId } = {}) {
    // Update comment instance in postComments collection
    const [updateCommentError] = await Firebase.getInstance()
      .updateFBData({
        doc: post.id,
        endCollection: this.commentsCollection,
        parentCollection: this.postCommentsCollection,
        values: {
          ...comment,
          updatedAt: Math.floor(new Date().getTime() / 1000)
        }
      })
      .then(() => [null])
      .catch(err => [err]);

    if (updateCommentError) {
      return [updateCommentError];
    }

    return this.updatePostPreviewComments({ post, groupId });
  }

  async deletePostComment({ post, comment, groupId } = {}) {
    // Delete comment instance first from postComments collection
    const [deleteCommentError] = await Firebase.getInstance()
      .deleteMultipleFBData({
        ids: [comment.id],
        doc: post.id,
        parentCollection: this.postCommentsCollection,
        endCollection: this.commentsCollection
      })
      .then(res => [null, res])
      .catch(err => [err]);

    if (deleteCommentError) {
      return [deleteCommentError];
    }

    return this.updatePostPreviewComments({ post, groupId, isDeletion: true });
  }

  getPostComments({ useInHooks = false, recentsOnly = false, queryOnly = false, postId }) {
    const query = Firebase.getInstance()
      .getFBCollectionData({
        endCollection: this.commentsCollection,
        parentCollection: this.postCommentsCollection,
        parentDoc: postId
      })
      .orderBy('date', 'desc');

    if (queryOnly || useInHooks) {
      if (recentsOnly) {
        return query.limit(3);
      }

      return query;
    }

    return query
      .get()
      .then(querySnapshot => {
        const data = [];
        querySnapshot.forEach(doc => {
          data.push({
            id: doc.id,
            ...doc.data()
          });
        });

        return [null, data];
      })
      .catch(error => {
        return [error];
      });
  }

  getPostComment({ postId, commentId }) {
    const query = Firebase.getInstance().getFBData({
      endCollection: this.commentsCollection,
      parentCollection: this.postCommentsCollection,
      parentDoc: postId,
      id: commentId
    });

    return query
      .get()
      .then(doc => [null, doc.data()])
      .catch(error => {
        return [error];
      });
  }

  async getRecentPostCommentArray({ userId, postId }) {
    const postRef = Firebase.getInstance().getFBData({
      parentCollection: this.postsCollection,
      endCollection: this.userPostsCollection,
      parentDoc: userId,
      id: postId
    });
    let postGet = await postRef.get();
    let postData = postGet.data();

    return postData.comments;
  }
}

export default new FirebasePostsServices();
